import type { AskedQuestion, ConversationMessage, WorkItemDetail, WorkItemStatus } from '@deskworks/shared-types';
import { TERMINAL_WORK_ITEM_STATUSES } from '@deskworks/shared-types';
import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CircleCheck, ListTodo, Loader2, MessagesSquare } from 'lucide-react';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { WaitingActions } from '@/components/actions/actions';
import { isQuestionBusy, roundLabel, rowState, type RoundRow, type TalkActivity } from '@/components/chat/desk-talk';
import { Markdown } from '@/components/markdown';
import { WorkItemStatusBadge } from '@/components/status-badge';
import { PendingApprovalCard } from '@/components/work-item/approval-card';
import { isAgentRunning } from '@/components/work-item/chat';
import { api } from '@/lib/api';
import { formatAbsolute, formatTime } from '@/lib/format';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useRealtimeStatus, useRealtimeSubscription, type RealtimeStatus } from '@/lib/realtime';
import { cn } from '@/lib/utils';

// Work accepted in a chat (docs/features/conversations.md#work-items-from-a-chat): the chat shows where it stands, live,
// and its result once it is done.

interface WorkItemMeta {
  workItemId: string;
  workItemTitle?: string;
  workItemStatus?: WorkItemStatus;
  event?: 'created' | 'complete' | 'failed' | 'needs_approval' | 'waiting' | 'cancelled' | string;
  /** Where the Work Item lives when that is not the chat's workspace (VICKI hands work to every workspace). */
  workspaceId?: string;
  /** The desk it went to (VICKI's chats). */
  deskName?: string;
  /** A sub-item of work VICKI handed out (docs/features/front-desk.md#following-the-work), and what it is part of. */
  sub?: boolean;
  parentWorkItemTitle?: string | null;
}

export function workItemMeta(m: ConversationMessage): WorkItemMeta | null {
  const meta = m.metadata as Partial<WorkItemMeta> | undefined;
  return m.senderType === 'system' && meta && typeof meta.workItemId === 'string' && meta.workItemId ? (meta as WorkItemMeta) : null;
}

/** A Work Item this chat has messages about: where its live card stands and what its messages last said. */
export interface ChatWorkItem {
  id: string;
  firstMessageId: string;
  lastMessageId: string;
  /** Its status as of its last message. */
  status: WorkItemStatus | null;
  /** The chat got its "done" message, which carries the result. */
  resultHere: boolean;
}

export function workItemsOfChat(messages: ConversationMessage[]): Map<string, ChatWorkItem> {
  const items = new Map<string, ChatWorkItem>();
  for (const m of messages) {
    const meta = workItemMeta(m);
    if (!meta) continue;
    const item = items.get(meta.workItemId) ?? { id: meta.workItemId, firstMessageId: m.id, lastMessageId: m.id, status: null, resultHere: false };
    item.lastMessageId = m.id;
    item.status = meta.workItemStatus ?? (meta.event && meta.event !== 'created' ? (meta.event as WorkItemStatus) : item.status);
    if (meta.event === 'complete') item.resultHere = true;
    items.set(item.id, item);
  }
  return items;
}

/** Not finished as far as the chat knows: only these are fetched and followed live. */
export function isOpenInChat(item: ChatWorkItem): boolean {
  return !item.status || !TERMINAL_WORK_ITEM_STATUSES.includes(item.status);
}

/** The item as it is now: live through the workspace's realtime, polled while the socket is down and it is open. */
function liveWorkItem(id: string, realtime: RealtimeStatus) {
  return queryOptions({
    queryKey: qk.workItemDetail(id),
    queryFn: () => api.workItems.get(id),
    refetchInterval: (q) => (realtime !== 'connected' && q.state.data && !TERMINAL_WORK_ITEM_STATUSES.includes(q.state.data.status) ? 8_000 : false),
  });
}

interface Phase {
  /** An agent (or the Lead) works on it right now: the card moves. */
  busy: boolean;
  text: string;
  /** It waits for the person: what to do. */
  ask?: string;
  tone: 'work' | 'attention' | 'blocked' | 'quiet';
}

/** What the item is doing, in the words of the chat. Null once finished: the badge and the result say it. */
function phaseOf(item: WorkItemDetail): Phase | null {
  if (TERMINAL_WORK_ITEM_STATUSES.includes(item.status)) return null;
  const running = isAgentRunning(item);
  if (item.paused) return { busy: false, text: t('Paused'), tone: 'quiet' };
  switch (item.status) {
    case 'working':
      return {
        busy: true,
        text: t('{agent} is working on it …', { agent: item.currentExecution?.agentName || item.assignedAgentName || t('An agent') }),
        tone: 'work',
      };
    case 'ready':
      return { busy: running, text: t('In the queue – starts as soon as an agent is free'), tone: 'quiet' };
    case 'created':
      return item.withLead || running
        ? { busy: running, text: t('With {lead} – it decides who takes it', { lead: item.lead?.name ?? t('the Lead') }), tone: 'work' }
        : { busy: false, text: t('Created – not started yet'), tone: 'quiet' };
    case 'waiting':
      if (item.waitingReason === 'children') return { busy: running, text: t('Waits for its sub-items'), tone: 'work' };
      if (item.withLead && !item.questionOpen) return { busy: running, text: t('{lead} checks the result …', { lead: item.lead?.name ?? t('the Lead') }), tone: 'work' };
      return { busy: running, text: item.waitingReason || t('Waiting for you'), ask: t('Waiting for you — reply now'), tone: 'attention' };
    case 'needs_approval':
      return { busy: false, text: t('Needs your approval'), ask: t('Needs your approval — take a look'), tone: 'attention' };
    case 'blocked':
      return { busy: false, text: item.waitingReason || t('Blocked'), ask: t('Take a look'), tone: 'blocked' };
  }
  return null;
}

const EVENT_LABEL: Record<string, string> = {
  created: t('Work Item created'),
  followed: t('Followed in this chat'),
  complete: t('Work Item done'),
  failed: t('Work Item failed'),
  needs_approval: t('Work Item needs your approval'),
  waiting: t('Work Item is waiting for you'),
  blocked: t('Work Item blocked'),
  cancelled: t('Work Item cancelled'),
  work_stalled: t('Work Item stalled'),
};

const SUB_EVENT_LABEL: Record<string, string> = {
  created: t('Sub-item started'),
  followed: t('Sub-item'),
  complete: t('Sub-item done'),
  failed: t('Sub-item failed'),
  needs_approval: t('Sub-item needs your approval'),
  waiting: t('Sub-item is waiting for you'),
  blocked: t('Sub-item blocked'),
  cancelled: t('Sub-item cancelled'),
  work_stalled: t('Sub-item stalled'),
};

function eventLabel(meta: WorkItemMeta, event = meta.event ?? 'created') {
  return (meta.sub ? SUB_EVENT_LABEL : EVENT_LABEL)[event] ?? (meta.sub ? t('Sub-item') : t('Work Item'));
}

/** Which desk has it and, for a sub-item, what it is part of. */
function ItemContext({ meta }: { meta: WorkItemMeta }) {
  const parts = [meta.deskName, meta.parentWorkItemTitle ? t('part of “{title}”', { title: meta.parentWorkItemTitle }) : null].filter(Boolean);
  return parts.length ? <div className="mt-0.5 truncate text-xs text-muted-foreground">{parts.join(' · ')}</div> : null;
}

/** A system message about a Work Item: its first one is the live card, "done" brings the result, the rest are notices. */
export function WorkItemCard({ message, item, workspaceId: chatWorkspaceId }: { message: ConversationMessage; item: ChatWorkItem; workspaceId: string }) {
  const meta = workItemMeta(message)!;
  const workspaceId = meta.workspaceId ?? chatWorkspaceId;
  const card =
    message.id === item.firstMessageId ? (
      <LiveWorkItemCard message={message} meta={meta} item={item} workspaceId={workspaceId} />
    ) : meta.event === 'complete' ? (
      <WorkItemResult message={message} meta={meta} workspaceId={workspaceId} />
    ) : (
      <WorkItemNotice message={message} meta={meta} workspaceId={workspaceId} latest={message.id === item.lastMessageId} />
    );
  // What it waits for a person to decide sits under its latest message, decided right here.
  if (message.id !== item.lastMessageId || !isOpenInChat(item)) return card;
  return (
    <div className="flex flex-col gap-2">
      {card}
      <ItemDecisions workItemId={item.id} workspaceId={workspaceId} />
    </div>
  );
}

/**
 * The approvals a Work Item waits for and the tool calls its agents asked for, right in the chat
 * (docs/features/front-desk.md#following-the-work): decided here or in the inbox, it is the same decision.
 */
function ItemDecisions({ workItemId, workspaceId }: { workItemId: string; workspaceId: string }) {
  const realtime = useRealtimeStatus();
  useRealtimeSubscription(workspaceId);
  const item = useQuery(liveWorkItem(workItemId, realtime)).data;
  if (item?.status !== 'needs_approval') return null;
  return (
    <div className="flex justify-center">
      <div className="flex w-full max-w-[92%] flex-col gap-2">
        {item.approvals
          .filter((a) => a.status === 'pending')
          .map((a) => (
            <PendingApprovalCard key={a.id} item={item} approval={a} />
          ))}
        <WaitingActions workItemId={item.id} />
      </div>
    </div>
  );
}

function CardHeader({ icon, label, createdAt, children }: { icon: ReactNode; label: string; createdAt: string; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      {icon}
      <span className="font-medium">{label}</span>
      <time dateTime={createdAt} title={formatAbsolute(createdAt, true)}>
        · {formatTime(createdAt)}
      </time>
      {children && <span className="ml-auto">{children}</span>}
    </div>
  );
}

function ItemLink({ workspaceId, workItemId, children, className }: { workspaceId: string; workItemId: string; children: ReactNode; className?: string }) {
  return (
    <Link to="/w/$workspaceId/work-items/$workItemId" params={{ workspaceId, workItemId }} className={className}>
      {children}
    </Link>
  );
}

/**
 * Where the item stands now. While it is open it follows the item live: who works on it and their latest step, with a
 * bar moving along the card; when it waits for the person, what to do. Once done the badge says so — the result comes
 * with the "done" message below, or here when the item reports to another chat.
 */
function LiveWorkItemCard({ message: m, meta, item, workspaceId }: { message: ConversationMessage; meta: WorkItemMeta; item: ChatWorkItem; workspaceId: string }) {
  const realtime = useRealtimeStatus();
  const open = isOpenInChat(item);
  // VICKI's work lives in other workspaces: their room brings its updates (the tray reads the same queries).
  useRealtimeSubscription(open ? workspaceId : undefined);
  const fetched = useQuery({ ...liveWorkItem(item.id, realtime), enabled: open }).data;
  // Once the chat says it is finished, a copy fetched while it ran no longer counts.
  const live = open || (fetched && TERMINAL_WORK_ITEM_STATUSES.includes(fetched.status)) ? fetched : undefined;
  const status = live?.status ?? item.status ?? meta.workItemStatus;
  const phase = live ? phaseOf(live) : null;
  const elsewhere = live?.status === 'complete' && !item.resultHere && live.originConversationId !== m.conversationId && live.result;
  return (
    <div className="flex justify-center">
      <div
        className={cn(
          'relative w-full max-w-[92%] overflow-hidden rounded-xl border bg-card px-3.5 py-3 shadow-xs transition-colors',
          phase?.busy && 'border-indigo-300 dark:border-indigo-800',
          phase?.tone === 'attention' && 'border-orange-300 dark:border-orange-800',
          phase?.tone === 'blocked' && 'border-red-200 dark:border-red-900',
        )}
        aria-busy={phase?.busy || undefined}
      >
        <CardHeader icon={<ListTodo className="size-3.5" />} label={eventLabel(meta)} createdAt={m.createdAt}>
          {status && <WorkItemStatusBadge status={status} paused={live?.paused} />}
        </CardHeader>
        <ItemLink workspaceId={workspaceId} workItemId={item.id} className="mt-1 block text-sm font-medium hover:text-primary hover:underline">
          {live?.title || meta.workItemTitle || t('Open Work Item')}
        </ItemLink>
        <ItemContext meta={meta} />
        {phase && (
          <div aria-live="polite" className="mt-1.5 flex flex-col gap-0.5 text-xs">
            <div
              className={cn(
                'flex items-center gap-1.5',
                phase.tone === 'work' ? 'text-indigo-700 dark:text-indigo-300' : phase.tone === 'attention' ? 'text-orange-700 dark:text-orange-300' : phase.tone === 'blocked' ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {phase.busy ? <Loader2 className="size-3.5 shrink-0 animate-spin" /> : <span className="size-1.5 shrink-0 rounded-full bg-current" />}
              <span className="min-w-0 break-words">{phase.text}</span>
            </div>
            {phase.busy && live?.currentActivity && <div className="truncate pl-5 text-muted-foreground italic">{live.currentActivity.message}</div>}
          </div>
        )}
        {phase?.ask && (
          <ItemLink
            workspaceId={workspaceId}
            workItemId={item.id}
            className={cn('mt-2 inline-flex items-center gap-1 text-xs font-medium hover:underline', phase.tone === 'blocked' ? 'text-destructive' : 'text-orange-700 dark:text-orange-300')}
          >
            {phase.ask} →
          </ItemLink>
        )}
        {elsewhere && <ResultText text={live.result!} />}
        {phase?.busy && (
          <div className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden bg-indigo-500/10" aria-hidden>
            <div className="animate-ws-sweep h-full w-1/3 rounded-full bg-indigo-500/70" />
          </div>
        )}
      </div>
    </div>
  );
}

/** Messages from before the chat got the whole result carried its first 3000 characters only. */
const CUT_RESULT = 3000;

/** The item is done: its result, in full — long ones open on click. */
function WorkItemResult({ message: m, meta, workspaceId }: { message: ConversationMessage; meta: WorkItemMeta; workspaceId: string }) {
  // "<Label> "<title>" is done.\n\n<result>": the title is the card's own line.
  const split = m.content.indexOf('\n\n');
  const said = split < 0 ? m.content : m.content.slice(split + 2);
  // An older message cut it off: the rest comes from the item.
  const item = useQuery({ queryKey: qk.workItemDetail(meta.workItemId), queryFn: () => api.workItems.get(meta.workItemId), enabled: said.length === CUT_RESULT }).data;
  const result = item?.result || said;
  return (
    <div className="flex justify-center">
      <div className="w-full max-w-[92%] rounded-xl border border-emerald-200 bg-card px-3.5 py-3 shadow-xs dark:border-emerald-900">
        <CardHeader icon={<CircleCheck className="size-3.5 text-emerald-600 dark:text-emerald-400" />} label={eventLabel(meta, 'complete')} createdAt={m.createdAt}>
          <WorkItemStatusBadge status="complete" />
        </CardHeader>
        <ItemLink workspaceId={workspaceId} workItemId={meta.workItemId} className="mt-1 block text-sm font-medium hover:text-primary hover:underline">
          {meta.workItemTitle || t('Open Work Item')}
        </ItemLink>
        <ItemContext meta={meta} />
        <ResultText text={result} />
      </div>
    </div>
  );
}

function ResultText({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [long, setLong] = useState(false);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    // Measured while closed: open, it has no limit to overflow.
    if (el && !open) setLong(el.scrollHeight > el.clientHeight + 4);
  }, [text, open]);
  return (
    <div className="mt-2 border-t pt-2">
      <div ref={ref} className={cn('relative', !open && 'max-h-80 overflow-hidden')}>
        <Markdown text={text} className="gap-1.5 text-[13px]" />
        {long && !open && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-card to-transparent" />}
      </div>
      {long && (
        <button type="button" onClick={() => setOpen((o) => !o)} className="mt-1 cursor-pointer text-xs font-medium text-primary hover:underline">
          {open ? t('Show less') : t('Show the whole result')}
        </button>
      )}
    </div>
  );
}

/** A later step of the item (it failed, waits, needs approval, was cancelled); what to do only while it is the latest. */
function WorkItemNotice({ message: m, meta, workspaceId, latest }: { message: ConversationMessage; meta: WorkItemMeta; workspaceId: string; latest: boolean }) {
  const event = meta.event ?? '';
  const attention = latest && (event === 'needs_approval' || event === 'waiting');
  return (
    <div className="flex justify-center">
      <div
        className={cn(
          'w-full max-w-[92%] rounded-xl border bg-card px-3.5 py-2.5 shadow-xs',
          event === 'failed' ? 'border-red-200 dark:border-red-900' : attention ? 'border-orange-300 dark:border-orange-800' : 'border-border',
        )}
      >
        <CardHeader icon={<ListTodo className="size-3.5" />} label={eventLabel(meta, event)} createdAt={m.createdAt} />
        <ItemLink workspaceId={workspaceId} workItemId={meta.workItemId} className="mt-1 block text-sm font-medium hover:text-primary hover:underline">
          {meta.workItemTitle || t('Open Work Item')}
        </ItemLink>
        <ItemContext meta={meta} />
        {/* A stall's message tells VICKI what to do; people read what happens. */}
        {event === 'work_stalled' ? (
          <div className="mt-1 text-[13px] text-muted-foreground">{t('Nothing has moved for 2 hours. VICKI looks what holds it.')}</div>
        ) : (
          m.content && <div className="mt-1 line-clamp-3 text-[13px] text-muted-foreground">{m.content}</div>
        )}
        {attention && (
          <ItemLink workspaceId={workspaceId} workItemId={meta.workItemId} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-orange-700 hover:underline dark:text-orange-300">
            {event === 'needs_approval' ? t('Needs your approval — take a look') : t('Waiting for you — reply now')} →
          </ItemLink>
        )}
      </div>
    </div>
  );
}

const TRAY_ROWS = 3;

/** The open Work Items of a chat as they are now, each with what it is doing (finished ones drop out). */
export function useOpenWorkItems(ids: string[]) {
  const realtime = useRealtimeStatus();
  return useQueries({ queries: ids.map((id) => liveWorkItem(id, realtime)) })
    .flatMap((q) => (q.data ? [q.data] : []))
    .flatMap((item) => {
      const phase = phaseOf(item);
      return phase ? [{ item, phase }] : [];
    });
}

/** One line of what moved: when, and in the words of the chat. */
interface Tick {
  text: string;
  at: number;
}

/**
 * The latest thing that moved in the work and the questions of this chat, like a ticker: a card that came in, an
 * agent's step, a desk's Lead asking its agents, an answer.
 */
function latestTick(messages: ConversationMessage[], open: { item: WorkItemDetail; phase: Phase }[], questions: AskedQuestion[], activity?: TalkActivity): Tick | null {
  const ticks: Tick[] = [];
  for (const m of messages.slice(-40)) {
    if (m.senderType !== 'system') continue;
    const at = Date.parse(m.createdAt);
    const meta = m.metadata as { event?: string; deskName?: string; desks?: string[]; fromName?: string; failed?: boolean };
    const item = workItemMeta(m);
    if (item) ticks.push({ text: `${eventLabel(item)}${item.workItemTitle ? `: ${item.workItemTitle}` : ''}`, at });
    else if (meta.event === 'talk_asked') ticks.push({ text: roundLabel(m, []), at });
    else if (meta.event === 'talk_answer' && meta.deskName) ticks.push({ text: meta.failed ? t('No answer from {name}', { name: meta.deskName }) : t('{desk} answers', { desk: meta.deskName }), at });
  }
  for (const { item, phase } of open) {
    const step = item.currentActivity;
    if (phase.busy && step) ticks.push({ text: `${step.agentName || item.currentExecution?.agentName || item.title}: ${step.message}`, at: Date.parse(step.createdAt) });
  }
  for (const q of questions) {
    for (const s of q.asking) {
      ticks.push({ text: t('{name} asks {other}', { name: q.to.name, other: s.to.name }), at: Date.parse(s.createdAt) });
      if (s.answeredAt) ticks.push({ text: s.status === 'answered' ? t('{name} answered', { name: s.to.name }) : t('No answer from {name}', { name: s.to.name }), at: Date.parse(s.answeredAt) });
    }
    const line = q.working ? activity?.get(q.talkId) : undefined;
    if (line) ticks.push({ text: `${q.to.name}: ${line.text}`, at: line.at });
  }
  return ticks.reduce<Tick | null>((latest, x) => (!latest || x.at >= latest.at ? x : latest), null);
}

/**
 * Above the composer: what this chat set going and is not done yet, live, so it stays in sight however far its card
 * has scrolled up — the questions to the desks (who answers, whom they ask) and the Work Items, newest first, each
 * sub-item under the item it is part of; one disappears once done (its result comes as a message). More than a few open
 * on click. At the bottom the latest thing that moved, and how much of it is done.
 */
export function WorkItemsTray({
  ids,
  rounds = [],
  questions = [],
  activity,
  messages = [],
  done,
}: {
  ids: string[];
  /** The rounds of questions to the desks still open: a line each. */
  rounds?: { message: ConversationMessage; rows: RoundRow[] }[];
  /** All questions asked from this chat: what moved among them shows in the activity line. */
  questions?: AskedQuestion[];
  activity?: TalkActivity;
  messages?: ConversationMessage[];
  /** Of all Work Items and questions of this chat, how many are finished. */
  done?: { n: number; of: number };
}) {
  const [all, setAll] = useState(false);
  const open = useOpenWorkItems(ids);
  if (!open.length && !rounds.length) return null;
  const items = asTree(open);
  const busy = items.filter((i) => i.phase.busy).length + rounds.filter((r) => r.rows.some((x) => isQuestionBusy(x.q))).length;
  const waiting = items.filter((i) => i.phase.tone === 'attention').length;
  const rows: ReactNode[] = [
    ...rounds.map((r) => <RoundTrayRow key={r.message.id} message={r.message} rows={r.rows} activity={activity} />),
    ...items.map(({ item, phase, depth }) => (
      <ItemLink
        key={item.id}
        workspaceId={item.workspaceId}
        workItemId={item.id}
        className="group flex min-w-0 items-center gap-2 rounded-md py-0.5 hover:bg-background/70"
      >
        {depth > 0 && <span className="shrink-0 text-muted-foreground" style={{ paddingLeft: (depth - 1) * 12 }}>↳</span>}
        {phase.busy ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-indigo-600 dark:text-indigo-300" />
        ) : (
          <span className={cn('mx-[3px] size-2 shrink-0 rounded-full', phase.tone === 'attention' ? 'bg-orange-500' : phase.tone === 'blocked' ? 'bg-red-500' : 'bg-muted-foreground/40')} />
        )}
        <span className="min-w-0 shrink truncate font-medium text-foreground/90 group-hover:underline">{item.title}</span>
        <span className={cn('min-w-0 flex-1 truncate max-sm:hidden', phase.tone === 'attention' ? 'text-orange-700 dark:text-orange-300' : 'text-muted-foreground')}>
          {phase.busy && item.currentActivity ? item.currentActivity.message : phase.text}
        </span>
        <WorkItemStatusBadge status={item.status} paused={item.paused} className="ml-auto shrink-0" />
      </ItemLink>
    )),
  ];
  const tick = latestTick(messages, open, questions, activity);
  return (
    <div className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-2.5 py-1.5 text-xs" aria-live="polite">
      <div className="flex items-center gap-1.5 font-medium text-muted-foreground">
        <ListTodo className="size-3.5" />
        {t('From this chat')}
        {busy > 0 && <span className="font-normal text-indigo-700 dark:text-indigo-300">· {t('{n} at work', { n: busy })}</span>}
        {waiting > 0 && <span className="font-normal text-orange-700 dark:text-orange-300">· {t('{n} wait for you', { n: waiting })}</span>}
      </div>
      {all ? rows : rows.slice(0, TRAY_ROWS)}
      {rows.length > TRAY_ROWS && (
        <button type="button" onClick={() => setAll((a) => !a)} className="cursor-pointer self-start pl-5 text-muted-foreground hover:text-foreground hover:underline">
          {all ? t('Show less') : t('and {n} more', { n: rows.length - TRAY_ROWS })}
        </button>
      )}
      {(tick || (done && done.of > 1)) && (
        <div className="mt-0.5 flex min-w-0 items-center gap-2 border-t pt-1 text-muted-foreground">
          <span className="shrink-0 font-mono text-[10px] tracking-wide uppercase">{t('Activity')}</span>
          {/* A new line fades in, so a change catches the eye. */}
          <span key={tick?.text} className="min-w-0 flex-1 truncate text-foreground/80 motion-safe:animate-in motion-safe:fade-in">
            {tick?.text}
          </span>
          {done && done.of > 1 && (
            <span className="ml-auto inline-flex shrink-0 items-center gap-1 font-mono" title={t('Work Items and questions of this chat: done of all')}>
              <CircleCheck className="size-3" />
              {done.n}/{done.of}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** An open round of questions in the tray: whom it asked, how many answered, and who is on it now. */
function RoundTrayRow({ message, rows, activity }: { message: ConversationMessage; rows: RoundRow[]; activity?: TalkActivity }) {
  const working = rows.find((r) => isQuestionBusy(r.q));
  const done = rows.filter((r) => r.q.status !== 'open').length;
  const now = working ? `${working.q.to.name}: ${rowState(working.q, working.q.working ? activity?.get(working.q.talkId)?.text : undefined)}` : null;
  return (
    <div className="flex min-w-0 items-center gap-2 py-0.5">
      {working ? (
        <Loader2 className="size-3.5 shrink-0 animate-spin text-indigo-600 dark:text-indigo-300" />
      ) : (
        <span className="mx-[3px] size-2 shrink-0 rounded-full bg-muted-foreground/40" />
      )}
      <span className="inline-flex shrink-0 items-center gap-1 font-medium text-foreground/90">
        <MessagesSquare className="size-3 text-primary" />
        {roundLabel(message, rows)}
      </span>
      <span className="min-w-0 flex-1 truncate text-muted-foreground max-sm:hidden">{now}</span>
      {rows.length > 1 && <span className="ml-auto shrink-0 font-mono text-muted-foreground">{t('{n} of {m} answered', { n: done, m: rows.length })}</span>}
    </div>
  );
}

/** Newest first, each item followed by its sub-items (those whose parent is open too), indented by depth. */
function asTree<T extends { item: WorkItemDetail }>(rows: T[]): (T & { depth: number })[] {
  const byId = new Map(rows.map((r) => [r.item.id, r]));
  const children = new Map<string, T[]>();
  const roots: T[] = [];
  for (const r of rows) {
    const parent = r.item.parentWorkItemId;
    if (parent && byId.has(parent)) children.set(parent, [...(children.get(parent) ?? []), r]);
    else roots.push(r);
  }
  const out: (T & { depth: number })[] = [];
  const walk = (r: T, depth: number) => {
    out.push({ ...r, depth });
    for (const c of children.get(r.item.id) ?? []) if (depth < 8) walk(c, depth + 1);
  };
  roots.forEach((r) => walk(r, 0));
  return out;
}
